import { encodePathVariable } from 'cadenza/api-client/api';
import type { AttributeTileAttribute } from 'cadenza/ui/attribute-tile/attribute-tile-attribute';
import {
  getAllAttributesFromObjectType,
  getAllEnrichmentsFromObjectType,
  getAttributeById,
  getObjecttypeById,
  getVisibleObjecttypes
} from 'cadenza/workbook/workbook.state';
import { store } from 'cadenza/store/cadenza-store';
import { AttributeCategory } from 'cadenza/repository/attribute-category';
import { AttributeType } from 'cadenza/repository/attribute-type';
import type { ObjecttypeIdString } from 'cadenza/repository/objecttype-id';
import { parseObjecttypeId } from 'cadenza/repository/objecttype-id';
import type { BaseGeometryType } from 'cadenza/repository/model/base-geometry-type';
import { AttributeModificationKey } from 'cadenza/workbook/api/attribute-modification-key';
import type { Objecttype } from 'cadenza/workbook/api/objecttype';
import { ObjecttypeBoundState } from 'cadenza/workbook/api/objecttype';
import type { WorkbookAttributeDto } from 'cadenza/workbook/api/workbook-attribute-dto';
import type { ObjecttypeWithAttributes } from 'cadenza/api-client/dto/objecttype-dto';
import type { AttributeId } from 'cadenza/workbook/model/attribute-id';
import type { ConditionDto } from 'cadenza/workbook/model/condition-dto';
import { isFeatureAvailable } from 'cadenza/features';
import { cadenzaUrl } from 'cadenza/utils/cadenza-url/cadenza-url';

export interface AttributeListController {
  accepts: (attribute: WorkbookAttributeDto) => { accepts: boolean };
}

/**
 * Filter all ObjectTypes available in the workbook with the given controller.
 *
 * @param controller - The controller used for filtering
 * @return Returns a deeply filtered list of ObjectTypes. If an ObjectType does not
 *     have any attribute left it is removed.
 */
export function filterAllObjectTypes (controller: AttributeListController) {
  const visibleObjectTypes = resolveObjecttypeAttributes(getVisibleObjecttypes(store.getState()));
  return filterObjecttypes(
    visibleObjectTypes,
    { attributePredicate: attribute => controller.accepts(attribute).accepts });
}

export function resolveObjecttypeAttributes (objecttypes: Objecttype[] | ObjecttypeWithAttributes[]): ObjecttypeWithAttributes[] {
  return objecttypes.map(objecttype => {
    return {
      ...objecttype,
      attributes: objecttype.attributes
        .map(attributeOrId => typeof attributeOrId === 'string'
          ? getAttributeById(store.getState(), attributeOrId)
          : attributeOrId)
        .filter(attribute => attribute.visible)
    };
  });
}

export function filterAllAttributesAndHierarchies (
  attributePredicate: (attribute: WorkbookAttributeDto) => boolean) {
  const filteredAttributes  = getVisibleObjecttypes(store.getState())
    .flatMap(objecttype => {
      const attributes = objecttype.attributes
        .map(attributeId => getAttributeById(store.getState(), attributeId));
      return attributes.filter(a => attributePredicate(a));
    });
  return addMissingHierarchyAttributesForLevels(filteredAttributes);
}

/**
 * In many situations, if an attribute list contains a hierarchy level without its hierarchy
 * attribute, problems can occur (example: AttributeChooser, AttributeList). To ensure that any
 * level in the given list has its actual hierarchy attribute as well, use this utility.
 */
export function addMissingHierarchyAttributesForLevels (attributes: WorkbookAttributeDto[]) {
  const hierarchies = new Map<AttributeId, WorkbookAttributeDto>();

  // Find all hierarchies that are in the list already
  attributes
    .filter(attr => attr.category === 'HIERARCHY')
    .forEach(hierarchy => {
      hierarchies.set(hierarchy.id, hierarchy);
    });

  // Add missing hierarchy attributes for hierarchy levels given
  for (const attribute of attributes) {
    const hierarchyId = attribute.hierarchyId;
    if (hierarchyId && !hierarchies.has(hierarchyId)) {
      const hierarchyAttribute = getAttributeById(store.getState(), hierarchyId);
      hierarchies.set(hierarchyId, hierarchyAttribute);
    }
  }
  return [ ...attributes, ...hierarchies.values() ];
}

function getAttribute (attributeOrId: AttributeId | WorkbookAttributeDto) {
  return (typeof attributeOrId === 'string')
    ? getAttributeById(store.getState(), attributeOrId)
    : attributeOrId;
}

/**
 * Check if thw two attribute or attribute ids given refer
 * to the same base attribute.
 *
 * @param a - first attribute
 * @param b - second attribute
 * @return <code>true</code> if a and b refer to the same base attribute
 * (or one of them is a base attribute and the other is an active attribute based on it).
 */
export function isSameBaseAttribute (a: AttributeId | WorkbookAttributeDto, b: AttributeId | WorkbookAttributeDto) {
  const { id: aId, baseId: aBaseId = aId } = getAttribute(a);
  const { id: bId, baseId: bBaseId = bId } = getAttribute(b);
  return (aBaseId === bBaseId);
}

interface FilterObjecttypeOptions {
  acceptedCategories: AttributeCategory[];
  acceptedTypes: AttributeType[];
  acceptedGeometryTypes: BaseGeometryType[];
  acceptedKeywords: string[];
  attributePredicate: (attribute: WorkbookAttributeDto) => boolean;
}

export function filterObjecttypes (
  objecttypes: ObjecttypeWithAttributes[],
  {
    acceptedCategories = [],
    acceptedTypes = [],
    acceptedGeometryTypes = [],
    acceptedKeywords = [],
    attributePredicate
  }: Partial<FilterObjecttypeOptions> = {}) {
  const filteredObjecttypes: ObjecttypeWithAttributes[] = [];

  // Empty means all are allowed
  acceptedCategories = acceptedCategories.length ? acceptedCategories : [ ...AttributeCategory.values ];
  acceptedTypes = acceptedTypes.length ? acceptedTypes : [ ...AttributeType.values ];

  objecttypes.forEach(objecttype => {
    const printNameMatchesKeywords = acceptedKeywords.length && match(objecttype, acceptedKeywords);

    const clone: ObjecttypeWithAttributes = {
      ...objecttype,
      attributes: []
    };

    let attributes = objecttype.attributes;
    if (objecttype.enrichments) {
      const enrichmentAttributes = objecttype.enrichments
        .flatMap(enrichment => getAllAttributesFromObjectType(store.getState(), enrichment.objecttypeId));
      attributes = [ ...objecttype.attributes, ...enrichmentAttributes ];
    }
    attributes = attributes.filter(({ category }) => acceptedCategories.includes(category));

    if (acceptedGeometryTypes.length > 0) {
      // if we have an accepted GeometryType defined, we do not care about the type, only the
      // geometry type is relevant
      // !: relatedGeometryType is always set on geometry attributes
      attributes = attributes.filter(({ relatedGeometryType }) => acceptedGeometryTypes.includes(relatedGeometryType!));
    } else {
      // Hierarchies don't have a type, so we just keep them in list.
      attributes = attributes.filter(({ category, type, hierarchyType }) =>
        hierarchyType || acceptedTypes.includes(type));
    }

    // Filter by keywords in attribute printNames
    if (!printNameMatchesKeywords && acceptedKeywords.length) {
      attributes = matchAttributes(attributes, acceptedKeywords);
    }

    // Additional validation
    if (attributePredicate) {
      attributes = attributes.filter(attributePredicate);
    }

    // Only add if there are attributes still left
    if (attributes.length) {
      // remove enrichments
      attributes = attributes.filter(({ objecttypeId }) => objecttypeId === objecttype.id);
      clone.attributes = attributes;
      filteredObjecttypes.push(clone);
    }
  });
  return filteredObjecttypes;
}

function matchAttributes (attributes: WorkbookAttributeDto[], keywords: string[]) {
  const matchingAttributes = [];
  for (let i = 0; i < attributes.length; i++) {
    const attribute = attributes[i];
    if (attribute.hierarchyType) {
      const hierarchyId = attribute.id;
      const hierarchyAttributes = [ attribute ];
      while (++i < attributes.length && attributes[i].hierarchyId === hierarchyId) {
        hierarchyAttributes.push(attributes[i]);
      }
      if (hierarchyAttributes.some(_ => match(_, keywords))) {
        matchingAttributes.push(...hierarchyAttributes);
      }
    } else if (match(attribute, keywords)) {
      matchingAttributes.push(attribute);
    }
  }
  return matchingAttributes;
}

function match ({ printName }: { printName: string }, keywords: string[]) {
  const lowerCasePrintName = printName.toLowerCase();
  return keywords.some(keyword => lowerCasePrintName.includes(keyword));
}

export function isModified (attribute: AttributeTileAttribute) {
  if (hasSettings(attribute)) {
    return AttributeModificationKey.values.some(mod => {
      // Only consider modifications, which can be set in the modifications dialog.
      return mod !== AttributeModificationKey.USE_LOCAL_HIERARCHY_LEVEL && mod in attribute.settings;
    });
  }
  return false;
}

function hasSettings (attribute: AttributeTileAttribute): attribute is AttributeTileAttribute & { settings: object } {
  return !!(attribute as AttributeTileAttribute & { settings: object }).settings;
}

interface FilterAttributeOptions {
  acceptedCategories: AttributeCategory[];
  acceptedKeywords: string[];
  attributePredicate: (attribute: WorkbookAttributeDto) => boolean;
}

export function filterAttributes (
  attributes: WorkbookAttributeDto[],
  options: Partial<FilterAttributeOptions> = {}) {
  const acceptedCategories = options.acceptedCategories && options.acceptedCategories.length ? options.acceptedCategories : AttributeCategory.values;
  const { acceptedKeywords = [] } = options;

  if (acceptedKeywords.length === 0) {
    return attributes;
  }
  const filteredAttributes: WorkbookAttributeDto[] = [];
  attributes.forEach(attribute => {
    if (acceptedKeywords.length > 0) {
      const attributePrintNameMatchesKeywords = acceptedKeywords.length && match(attribute, acceptedKeywords);
      const objecttypePrintNameMatchesKeywords = acceptedKeywords.length && match({ printName: attribute.objecttypePrintName }, acceptedKeywords);
      if (!attributePrintNameMatchesKeywords && !objecttypePrintNameMatchesKeywords) {
        return;
      }
    }
    if (acceptedCategories.includes(attribute.category)) {
      filteredAttributes.push(attribute);
    }
  });

  return filteredAttributes;
}

/**
 * Finds an object type related to which the condition belongs to (via the first attribute). Throws
 * an error if none is found.
 *
 * @param objecttypes - The objecttypes where to look
 * @param condition - The condition to which the object type needs to be found
 * @return object type the condition belongs to
 */
export function findObjecttype (objecttypes: ObjecttypeWithAttributes[], condition: ConditionDto) {
  const firstAttributeId = condition.attributeIds[0];
  for (const objecttype of objecttypes) {
    for (const attribute of objecttype.attributes) {
      if (attribute.id === firstAttributeId) {
        return objecttype;
      }
    }
  }
  throw new Error('Could not find objecttype containing attributeId: ' + firstAttributeId);
}

export function isLobAttributeWithoutRelation (attribute: WorkbookAttributeDto) {
  return attribute.type === 'LOB' && !attribute.hasLobRelation;
}

export function getGroupedEnrichmentObjecttypeIdsFromObjecttype (objecttypeId: ObjecttypeIdString) {
  const enrichments = getAllEnrichmentsFromObjectType(store.getState(), objecttypeId);
  const result = [];
  for (const enrichment of enrichments) {
    if (enrichment.grouped) {
      result.push(enrichment.objecttypeId);
    }
  }
  return result;
}

export function createEditAttributeColoringUrl (attribute: WorkbookAttributeDto) {
  // BOT id
  const objecttypeId = attribute.underlyingObjectTypeId ?? attribute.objecttypeId;
  const objecttype = getObjecttypeById(store.getState(), attribute.objecttypeId);

  const isUnboundObjecttype = objecttype.boundState === ObjecttypeBoundState.UNBOUND;
  const isAdministrationAvailable = isFeatureAvailable('ADMINISTRATION')
    && getObjecttypeById(store.getState(), objecttype.id).privileges.includes('visible');

  if (isUnboundObjecttype && isAdministrationAvailable) {
    const tab = 'coloring';
    const [ repositoryName, objecttypeName ] = parseObjecttypeId(objecttypeId);
    return attribute.hierarchyId
      // when the attribute is part of a hierarchy, the name is no longer matching the member name
      ? cadenzaUrl(`/admin/repositories/${repositoryName}/objecttypes/${objecttypeName}/members/${encodePathVariable(attribute.objecttypeAttributeName!)}/?tab=${tab}`)
      : cadenzaUrl(`/admin/repositories/${repositoryName}/objecttypes/${objecttypeName}/members/${encodePathVariable(attribute.name)}?tab=${tab}`);
  }
}

export function workbookAttributeHasNameRelation (attribute: WorkbookAttributeDto) {
  const attrId = attribute.baseId;
  return getObjecttypeById(store.getState(), attribute.objecttypeId).attributeRelationGroups?.some(
    group =>
      group.nameAttribute === attrId
      || group.nameAttribute
      && [ ...group.idAttributes, ...group.lobAttributes, group.geometryAttribute ].includes(attrId)
  );
}
